// Live apply (WS-49, contract section 4): the one interface the chrome, the tour engine and the editor drive the map
// through, exposed as window.resortMap.runtime (shape: settings/typedefs.js `Runtime`). It HOLDS the layers of
// contract section 3 and asks `resolve` of the settings package for the result; it never layers anything itself.
// Engine modules register an applier per key; a key without one is `rebuild`. Pure: no DOM, no three.js.
import { resolve } from './settings/index.js';
import { isLegacy, legacyStored } from './legacy.js';
/** @typedef {import('./settings/typedefs.js').Runtime} Runtime */
/** @typedef {import('./settings/typedefs.js').SetResult} SetResult */

// The same value whatever the order of its keys: the database hands a saved preset back with every object's keys
// reordered (jsonb), and the opening view { position, target, fov } read as changed flew the camera to it on Save.
const sorted = (_, x) => (x && typeof x === 'object' && !Array.isArray(x) ? Object.fromEntries(Object.keys(x).sort().map((k) => [k, x[k]])) : x);
const same = (a, b) => a === b || JSON.stringify(a, sorted) === JSON.stringify(b, sorted);
const pad = (n) => String(n).padStart(2, '0');

// ---- day and zone maths (time.date, site.tz) ---------------------------------------------------------------------
/** `"MM-DD"` (or today's unpadded `"M-D"`) as { month, day }, or null. */
export function monthDay(s) {
  const [m, d] = String(s ?? '').split('-').map(Number);
  return m >= 1 && m <= 12 && d >= 1 && d <= 31 ? { month: m, day: d } : null;
}
export const toMonthDay = (month, day) => `${pad(month)}-${pad(day)}`;

/**
 * Hours east of UTC of a zone at an instant. A number passes through (today's site.utcOffset), `"+02:00"` is a
 * fixed offset, an IANA name (`Europe/Helsinki`) follows its daylight-saving rules through Intl. Unknown = null.
 */
export function zoneOffset(tz, ms = Date.now()) {
  if (typeof tz === 'number') return tz;
  if (typeof tz !== 'string' || !tz) return null;
  const m = /^([+-])(\d{2}):?(\d{2})$/.exec(tz);
  if (m) return (m[1] === '-' ? -1 : 1) * (Number(m[2]) + Number(m[3]) / 60);
  try {
    const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' });
    const p = Object.fromEntries(f.formatToParts(ms).map((x) => [x.type, Number(x.value)]));
    return (Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ms / 1000) * 1000) / 3600000;
  } catch { return null; } // errors:ignore: not a zone name
}

// ---- remembered values (contract section 3): mms:<map>:<preset>:<key>, JSON, one preset never leaks into another
export const storeKey = (map, preset, key) => `mms:${map}:${preset}:${key}`;
/** Raw stored values of one preset, for `resolve` layer 7b. */
export function presetStored(storage, map, preset) {
  const out = {}, prefix = storeKey(map, preset, '');
  try {
    for (let i = 0; i < (storage?.length ?? 0); i++) { const k = storage.key(i); if (k.startsWith(prefix)) out[k] = storage.getItem(k); }
  } catch { /* errors:ignore: storage blocked */ }
  return out;
}

const LAYER = { visitor: 'visitor', tour: 'tour', editor: 'editor', preset: 'preset' };

/**
 * @param {object} o
 * @param {import('./settings/typedefs.js').Registry} o.registry
 * @param {import('./settings/typedefs.js').Layers} o.layers   resort, content, bundle, preset, placement, url, host
 * @param {Storage | null} [o.storage]  the browser storage (null = nothing is read or remembered)
 * @param {() => number} [o.now]
 * @returns {{ api: Runtime } & object}  `api` is the typed interface; the rest is for main.js and config.js only
 */
export function createRuntime({ registry: reg, layers, storage = null, now = Date.now }) {
  // WS-51: `layers.visitor` and `layers.editor` are given only by a page that comes back from the editor's `reload`
  // (config.js `takeReload`): what the visitor had pressed and the editor's unsaved values are in force before
  // anything is built, so a `rebuild` key takes effect and the time, the season and the selection are the same.
  const L = { ...layers, registry: reg, visitor: { ...layers.visitor }, tour: {}, editor: { ...layers.editor } };
  const host = L.host || {};
  let loaded = !!layers.loaded; // the preset on the page came through loadPreset (the editor's unsaved copy)
  const appliers = new Map(), listeners = new Map();
  let probe = { view: () => null, screen: () => ({}) };
  let state = null, pending = null, seenKeys = new Set(), applying = 0;

  const emit = (event, payload) => {
    for (const fn of [...(listeners.get(event) || [])]) { try { fn(payload); } catch (err) { console.error(`runtime: ${event} listener failed`, err); } }
  };
  const canon = (key) => { const e = reg.entry(key); return !e || e.family ? key : e.key; };
  const read = (s, key) => { const k = canon(key); return k in s.values ? s.values[k] : reg.entry(k)?.default; };
  // A season change moves the light to that season's own date (resort ui.seasons[].date), as the season switch on the
  // bar does: winter -> summer -> winter is back under 21 December's sun, not June's. A patch that names its own date
  // keeps it (a tour stop, Renders); clearing the season clears the date it brought.
  const seasonDate = (id) => { const d = monthDay(L.resort?.ui?.seasons?.find((s) => s.id === id)?.date); return d && toMonthDay(d.month, d.day); };
  const seasonDated = (patch, layer = {}) => {
    const p = { ...patch }, ks = Object.keys(p), k = ks.find((x) => canon(x) === 'season.current'), v = p[k];
    const d = k && !ks.some((x) => canon(x) === 'time.date') && seasonDate(v ?? layer['season.current']);
    if (d && v !== undefined) p['time.date'] = d;
    else if (d && layer['time.date'] === d) p['time.date'] = undefined; // (the date this rule wrote: a date set apart stays)
    return p;
  };
  const readStored = () => (isLegacy(L.preset) ? legacyStored(reg, storage, host.resort) : presetStored(storage, host.map, L.preset?.id));
  // Remembered under a real preset: what today's page remembers (keys with a legacy store) and the language.
  const remember = (keys) => {
    if (!storage || isLegacy(L.preset)) return; // the legacy presets keep today's keys, written by their own modules
    for (const key of keys) {
      const e = reg.entry(key), k = storeKey(host.map, L.preset?.id, key);
      if (!e || !(e.legacy?.store || e.group === 'language')) continue;
      try { if (key in L.visitor) storage.setItem(k, JSON.stringify(L.visitor[key])); else storage.removeItem(k); } catch { /* errors:ignore: storage blocked */ }
    }
  };

  // `resolve` is pure: the clock for time.mode `real` and season.mode `calendar` is handed in (host.now)
  const run = () => resolve({ ...L, host: { ...host, now: now() } });

  // Moves the page from the state on screen to `next`: one applier call per changed key, then the events.
  // `direct` = the keys the caller named (the others changed as a consequence, e.g. through the season patch);
  // `skip` = keys the engine already shows (the visitor pressed its own control).
  function apply(next, { source, ms = 0, direct = null, skip = null }) {
    const prev = state;
    state = next;
    const keys = [...new Set([...Object.keys(prev.values), ...Object.keys(next.values)])].filter((k) => !same(read(prev, k), read(next, k)));
    const applied = [], rebuild = [];
    applying++; // an applier that moves an engine control must not come back as a visitor's press (seen)
    for (const key of keys) {
      const e = reg.entry(key), fns = appliers.get(e?.key);
      let live = e?.apply === 'live' && !!fns;
      if (live && !skip?.has(key)) for (const fn of fns) {
        try { if (fn(read(next, key), { key, previous: read(prev, key), source, ms, values: next.values, direct: !direct || direct.has(key) }) === false) live = false; }
        catch (err) { console.error(`runtime: applier of ${key} failed`, err); live = false; }
      }
      (live ? applied : rebuild).push(key);
    }
    applying--;
    if (keys.length || !same(prev.shown, next.shown)) emit('change', { keys, values: next.values, source, shown: next.shown });
    if (rebuild.length) emit('rebuild-needed', { keys: rebuild });
    return { applied, rebuild };
  }

  // What the visitor changed through the engine's own controls reaches the visitor layer here, a little later
  // (one resolve for a burst of presses, not one per frame of a time-lapse).
  function flush() {
    if (!pending) return;
    clearTimeout(pending); pending = null;
    const skip = seenKeys; seenKeys = new Set();
    apply(run(), { source: 'visitor', direct: skip, skip });
  }

  /** @type {Runtime} */
  const api = Object.freeze({
    get(key) { flush(); return key === undefined ? { ...state.values } : read(state, key); },
    // owner 2026-10-31 (the tour's own look, Q3 A): the map's own value, without what the visitor chose (presses, the
    // address, the browser's memory) and without a tour; the owner's layers (and the editor's unsaved values) count
    base(key) {
      flush();
      const s = resolve({ ...L, visitor: {}, stored: {}, url: {}, tour: {}, host: { ...host, now: now() } });
      return key === undefined ? { ...s.values } : read(s, key);
    },
    set(patch, { source = 'visitor', ms = 0 } = {}) {
      flush();
      patch = patch && typeof patch === 'object' ? patch : {};
      if (!LAYER[source]) return { applied: [], rebuild: [], rejected: Object.keys(patch).map(canon).map((key) => ({ key, reason: 'unknown-source' })) };
      patch = seasonDated(patch, source === 'preset' ? L.preset?.values : L[source]);
      const keys = Object.keys(patch).map(canon);
      const before = source === 'preset' ? { ...L.preset?.values } : { ...L[source] };
      const write = (drop) => {
        const o = { ...before };
        for (const [k, v] of Object.entries(patch)) { const key = canon(k); if (drop.has(key)) continue; if (v === undefined) delete o[key]; else o[key] = v; }
        if (source === 'preset') L.preset = { ...L.preset, values: o }; else L[source] = o;
      };
      write(new Set());
      let next = run();
      // a refused value never stays in the layer: the earlier value of that layer is back in force
      const rejected = next.rejected.filter((r) => r.layer === source && keys.includes(canon(r.key))).map(({ key, reason }) => ({ key: canon(key), reason }));
      const bad = new Set(rejected.map((r) => r.key));
      if (bad.size) { write(bad); next = run(); }
      const ok = keys.filter((k) => !bad.has(k));
      const { rebuild } = apply(next, { source, ms, direct: new Set(ok) });
      if (source === 'visitor') remember(ok);
      return { applied: ok.filter((k) => !rebuild.includes(k)), rebuild, rejected };
    },
    loadPreset(doc) {
      flush();
      L.preset = doc; loaded = true;
      L.stored = readStored();
      const next = run();
      const out = apply(next, { source: 'preset' });
      return { ...out, rejected: next.rejected.filter((r) => r.layer === 'preset' || r.layer === 'placement').map(({ key, reason }) => ({ key, reason })) };
    },
    controls() { flush(); return reg.controls.map((c) => ({ id: c.id, shown: state.shown.includes(c.id) })); },
    capture() { flush(); return { view: probe.view(), values: { ...JSON.parse(JSON.stringify(state.values)), ...probe.screen() } }; },
    on(event, fn) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event).add(fn);
      return () => listeners.get(event).delete(fn);
    },
    register(key, fn) { const k = reg.entry(key)?.key ?? key; appliers.set(k, [...(appliers.get(k) || []), fn]); },
  });

  L.stored = readStored();
  state = run();
  return {
    api, emit, registry: reg,
    get legacy() { return isLegacy(L.preset); },
    get preset() { return L.preset; },
    ids: { map: host.map, placement: L.placement?.id || null, get preset() { return L.preset?.id || null; } },
    /** WS-51 `reload`: the layers a reload of the editor frame must bring back (the preset only when the editor loaded its own copy). */
    keep() { flush(); return JSON.parse(JSON.stringify({ visitor: L.visitor, editor: L.editor, preset: loaded ? L.preset : null })); },
    /** main.js: how to read the camera and the values on screen, for capture(). */
    bind(p) { probe = { ...probe, ...p }; },
    /** The engine's own control changed a value on screen (no applier runs for it). */
    seen(patch) {
      if (applying) return;
      for (const [k, v] of Object.entries(patch)) { const key = canon(k); L.visitor[key] = v; seenKeys.add(key); }
      remember(Object.keys(patch).map(canon));
      pending ||= setTimeout(flush, 200);
    },
    /** The content of the season on screen (layer 3 patches resort.json only, so the season's content is handed in). */
    content(c) { L.content = c; if (!applying) flush(); apply(run(), { source: 'preset', direct: new Set() }); },
    /** VIS-73: the editor draws the hidden controls as ghosts that can be pressed (main.js, bridge state): a press on
     *  one reaches its settings (resolve `host.ghosts`); off again (preview, the editor gone), it does not. */
    ghosts(on) { if (!!host.ghosts === !!on) return; flush(); host.ghosts = !!on; apply(run(), { source: 'editor' }); },
    /** Follows the clock (`real`, `calendar`); call it now and then. */
    tick() { flush(); const v = state.values; if (v['time.mode'] === 'real' || v['season.mode'] === 'calendar') apply(run(), { source: 'preset', ms: 1000 }); },
    shown(id) { return state.shown.includes(id); },
    source(key) { return state.sources[canon(key)] || 'default'; },
    /** An address parameter, or null when it is absent or belongs to a control that is not shown. */
    param(name) {
      const c = reg.controls.find((x) => x.url.includes(name));
      return c && !state.shown.includes(c.id) ? null : new URLSearchParams(L.url || {}).get(name);
    },
    /** False when the key is a shortcut of controls (or of control `id`) none of which is shown. */
    keyOk(key, id = null) {
      const own = reg.controls.filter((c) => c.legacy?.keys?.includes(key) && (!id || c.id === id));
      return !own.length || own.some((c) => state.shown.includes(c.id));
    },
    appliers: () => [...appliers.keys()],
  };
}
